import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import {
  incomingThumbPathOf,
  isPhotoPrivacyFinalFailure,
  isPhotoStatusPublished,
  isDefaultPostAnalysis,
  POST_ANALYSIS_RESULTS_FIELD,
  readPostAnalysisResult,
  type AiAnalysisBousaiResult,
  type AiAnalysisWalkerResult,
  type DeviceOrientationSample,
  type DistrictPhotoConfig,
  type GeoPoint,
  type LocationSource,
  type PostAiAnalysis,
  type PostReanalysisRequest,
  type PostPhotoMeta,
  type PostPhotoPrivacy,
  type PostPhotoPrivacyStatus,
  type PostVisibility,
} from '@citizen-walk/shared-types';
import { auth, db } from '../../lib/firebase';
import { addToOutbox, deleteFromOutbox, type OutboxPost } from './outbox';
import { addOptimisticPost, removeOptimisticPost } from './optimisticPosts';
import { assertPhotoSize, generateThumbnail, preprocessPhoto } from './photo';
import { deletePhotoFromStorage, ownIncomingPaths, uploadIncomingPhoto } from './uploadPhoto';

/**
 * 投稿（最小フォーム）。Phase 2 minimum: title / comment / geolocation / visibility。
 * 写真は Phase 4+ で追加 (docs/04 §1-1)。
 *
 * Path: `/districts/{districtId}/posts/{postId}` (docs/04 §4-2)。
 * districtId は呼出側で解決済の地区 (`useDistrict().id`) を必須で渡す
 * (地区フォールバック根絶 2026-06-08: 旧 phase2-default 既定は撤去)。
 *
 * Visibility (REQ-4.1.18):
 *   - 'public': 全認証ユーザーが閲覧可
 *   - 'hidden_others': 投稿者本人のみ閲覧
 *   - 'hidden_all': 管理者のみ閲覧 (現状 user app 側は表示しない)
 */
export interface PostMinimal {
  id: string;
  authorUid: string;
  authorNickname: string;
  title: string;
  comment: string | null;
  location: GeoPoint | null;
  accuracy: number | null;
  source: LocationSource | null;
  visibility: PostVisibility;
  /**
   * 紐づくセッション ID (user 第 2 弾で createPost に追加、user 第 3 弾で 一覧 filter に使用)。
   * 旧 Post (sessionId 未保存) は null として読み出し、filter UI からは「未紐付け」として除外扱い。
   */
  sessionId: string | null;
  createdAt: Timestamp | null;
  /** Storage path (Phase 5 photo client upload、REQ-4.1.10)。 */
  photoPath: string | null;
  photoThumbPath: string | null;
  /**
   * 原寸画像の download URL (docs/106 オフライン再表示)。doc に保存済なら表示層が
   * 楽観表示に使う。旧投稿 / 写真なしは null。
   */
  photoUrl: string | null;
  /** サムネ画像の download URL (docs/106)。trigger で失効しうるため表示層が自己修復する。 */
  photoThumbUrl: string | null;
  /** 写真の幾何メタ (docs/27、360度写真なら projection='equirectangular')。旧投稿は undefined/null。 */
  photo?: PostPhotoMeta | null;
  /**
   * 写真の顔・ナンバーのぼかし処理の状態 (docs/28 §8-2)。導入前の投稿・写真なしは null。
   * 投稿者本人の画面で「処理中」「処理に失敗」の注意書きを出すのに使う。
   */
  photoPrivacyStatus?: PostPhotoPrivacyStatus | null;
  /**
   * ぼかし前の写真 (元画像) の Storage パス (docs/28 §5-2)。読めるのは投稿者本人だけ (storage.rules)。
   * 「自分の写真のダウンロード」でぼかし前の写真を渡すのに使う (§5-4)。
   */
  photoOriginalPath?: string | null;
  /** ぼかし処理を待っている写真の一時置き場 (docs/28 §4-3)。読めるのは投稿者本人だけ。 */
  photoIncomingPath?: string | null;
  /** 最後に写真を公開した時刻 (docs/28)。編集・作り直しの処理中も前の写真をほかの人に見せる判定に使う。 */
  photoPrivacyPublishedAt?: number | null;
  /** ぼかし処理が最終的に失敗した (自動の再実行を使い切った・恒久的なエラー、§4-9)。 */
  photoPrivacyFinalFailure?: boolean;
  /**
   * 写真をこの閲覧者には出さない (ぼかしが終わっていない・失敗・公開しない、§4-3 / §4-9)。
   * true のとき写真のパス・URL は null に差し替え済みで、画面は「写真は準備中です」を出す。
   * 投稿者本人には常に false (本人は処理前の写真を見られる、D15)。
   */
  photoWithheld?: boolean;
  /** onAgreeWrite trigger (af139dd) が集計、初期値 0。docs/98 R-13 で 0 未満 clamp 検討中。 */
  agreeCount: number;
  /**
   * AI 解析結果の status 部分のみ (Phase 5.5 minimum)。
   * full 形 (mode/ecw/rawOutput/error) は Phase 5 後続で Renderer 8 種実装時に拡張。
   * trigger / callable が undefined → 'done' に書込、未解析 post は undefined。
   */
  aiAnalysisStatus: 'pending' | 'processing' | 'done' | 'failed' | 'skipped' | null;
  /** AI 解析失敗時の message (status='failed' のときのみ)。 */
  aiAnalysisError: string | null;
  /**
   * ECW mode の AI 解析結果 (description / emotions / categories)。
   * status === 'done' && mode === 'ecw' のときに値あり、それ以外は null。
   * minimum Renderer (PostDetailModal で textbox 表示) 用、フル Renderer は Phase 5 後続。
   */
  aiAnalysisEcw: {
    description: string;
    emotionText: string;
    emotions: string[];
    emotionReason: string;
    categoryCodes: string[];
    categoryReason: string;
  } | null;
  /**
   * Bousai (防災) mode の AI 解析結果 (user 第 10 弾、Phase 5.5)。
   * status === 'done' && mode === 'bousai' のときに値あり、それ以外は null。
   * classifications[].category_code が category filter / Renderer の source。
   */
  aiAnalysisBousai: AiAnalysisBousaiResult | null;
  /**
   * Walker (ウォーカブル) mode の AI 解析結果 (user 第 10 弾、Phase 5.5)。
   * status === 'done' && mode === 'walker' のときに値あり、それ以外は null。
   * classifications[].category_code が category filter / Renderer の source。
   */
  aiAnalysisWalker: AiAnalysisWalkerResult | null;
  /**
   * AI 解析の raw response (Sprint X2 で本格 Renderer 用に exposure)。
   * `status === 'done'` のときに値あり (analyzePost が text + parsed を保存)。
   * PostDetailModal で「raw JSON を表示」debug 折畳に使用。
   */
  aiAnalysisRaw: { text: string; parsed: unknown } | null;
  /** AI 解析完了日時 (ISO8601、`status === 'done'` のときに値あり)。 */
  aiAnalysisAnalyzedAt: string | null;
  /** AI 解析使用 settings ID (M1 master 参照、debug / 監査用)。 */
  aiAnalysisSettingsId: string | null;
  /**
   * 列名動的化レベル 3 (docs/105) の汎用 AI 解析結果 (mode 非依存)。
   * 出力フォーマット (M3) に `fields[]` が設定されている場合、analyzePost が
   * `validateGenericPayload` で sanitize した任意キーの出力をそのまま保存する。
   * 非空のとき GenericAiResultBlock が動的列の生データを表形式 / chip 等で描画する
   * (mode 別 result と併存)。未設定 / 空オブジェクトは null。
   */
  aiAnalysisGeneric: Record<string, unknown> | null;
  /**
   * titleGeneration (M1.titleGeneration.enabled) が生成した AI サブタイトル (1 文)。
   * 投稿詳細でユーザー投稿タイトルの直下に表示する。無効 / 未生成は null。
   * (既存 PostMinimal リテラル互換のため optional。mapPostDoc は常に string|null を設定する。)
   */
  aiAnalysisGeneratedTitle?: string | null;
  /**
   * docs/29: 追加の AI 解析の観点ごとの結果 (`post.analysisResults.{key}.aiAnalysis` の生の形)。
   * 投稿詳細で `projectPostAnalysis` を通して上の aiAnalysis* と同じ形に写して表示する。無ければ undefined。
   */
  analysisResults?: Record<string, PostAiAnalysis>;
  /**
   * 投稿者から管理者への AI 再解析の依頼があるか (`post.reanalysisRequest`、docs/13 §9-2)。
   * 管理者が再解析すると Functions が依頼を消す。既存 PostMinimal リテラル互換のため optional。
   */
  reanalysisRequested?: boolean;
  /**
   * 投稿者 (admin / 投稿アプリ rating-scale 等) が入力する付帯 field。
   * Sprint X5b で HappinessScore / HealthScore を expose (docs/04 §1-1 PostDoc.extraFields)。
   * 旧 ECW では `customFields` と `extraFields` が分岐していたが、v2 は extraFields 一本。
   * 値の型は Firestore PostDoc 準拠で unknown (number / string / boolean / array 等)。
   */
  extraFields: Record<string, unknown> | null;
  /**
   * このクライアントのローカル書込みがまだサーバに同期されていない (Firestore
   * `snapshot.metadata.hasPendingWrites`)。オフライン時に `navigator.onLine===true` のまま
   * setDoc した投稿 (Firestore オフラインキャッシュにキュー) を「未送信」として識別するために使う。
   * outbox 経路と合わせて、地図で未送信投稿を pending マーカーで区別表示する (REQ-4.1.15)。
   */
  pendingWrite?: boolean;
}

export interface CreatePostInput {
  authorUid: string;
  authorNickname: string;
  title: string;
  comment?: string;
  /** 取得位置。Phase 2 minimum では必須運用、Phase 3+ で optional 判定（地区設定で必須/任意切替）。 */
  location: GeoPoint;
  /** 位置精度（メートル）。GPS の場合のみ値を持つ。 */
  accuracy?: number | null;
  /** 取得元。Phase 2 は GPS のみだが Phase 4 で manual / map-click も追加予定 (docs/04 §4-2)。 */
  source?: LocationSource;
  /** 可視性。省略時は 'public' (旧 lib/posts.ts:162 デフォルト踏襲)。 */
  visibility?: PostVisibility;
  /** 投稿先 districtId (必須)。呼出側で解決済の `useDistrict().id` を渡す。 */
  districtId: string;
  /**
   * 紐づくセッション ID (user 第 2 弾、admin 第 16 弾の後追い、docs/04 §4-2 Post.sessionId)。
   * 未選択 = `null` で Firestore に書き込む (フィルタからは "全セッション" 扱い)。
   */
  sessionId?: string | null;
  /** 写真 (REQ-4.1.10、Phase 5)。client で preprocess + thumbnail 生成 → Storage upload */
  photo?: File | null;
  /**
   * 地区別 投稿写真設定 (Task C、`useDistrict().photoConfig` 素通し)。
   * 圧縮パラメータ / 無圧縮 / 受付上限を制御。未指定は既定 (圧縮あり・1600px・1.5MB・10MB) で従来動作。
   */
  photoConfig?: DistrictPhotoConfig;
  /**
   * 写真の幾何メタ (docs/27)。PostForm が `detectPanorama` で元ファイルから判定した結果。360度写真は
   * 前処理サイズが変わり (4096px)、投稿 doc の `photo` に保存される。平面写真は省略可。
   */
  photoMeta?: PostPhotoMeta | null;
  /**
   * デバイス方位センサーサンプル (Sprint BF Part 1、GAP-U1)。
   * `useDeviceOrientation().getCurrentOrientation()` の戻り値を渡す。
   * 取得失敗 / 未対応端末では undefined / null で Firestore には書込まない。
   */
  orientation?: DeviceOrientationSample | null;
  /**
   * 投稿者入力の付帯 field 値 (Sprint BG-DataSettings 5)。
   * PostExtraFieldsForm で district.dataSettings.postFields に基づき動的に収集した値を、
   * そのまま PostDoc.extraFields に書き込む。empty / undefined は書き込まない。
   */
  extraFields?: Record<string, unknown> | null;
}

export type PostsState =
  | { status: 'loading'; posts: []; error: null }
  | { status: 'ready'; posts: PostMinimal[]; error: null }
  | { status: 'error'; posts: []; error: Error };

function postsCollection(districtId: string) {
  return collection(db, 'districts', districtId, 'posts');
}

/**
 * outbox item の安定クライアント ID を生成 (冪等送信用)。Firestore doc ID として使う。
 * crypto.randomUUID があれば採用、無ければ時刻 + 乱数で代替 (doc ID として有効な文字列)。
 */
function newClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `cw-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

/**
 * online 投稿で `setDoc` のサーバ ack を待ちすぎてフォームが固まるのを防ぐタイムアウト
 * (docs/handoff_2026-06-03 根本対処)。
 *
 * Android 機内モードでは `navigator.onLine` が `true` のまま残るため createPost が online
 * 経路に入るが、実際はオフラインで `setDoc` の promise がサーバ ack まで解決しない。これを
 * 一定時間で打ち切り、楽観的に postId を返す (ローカル書込はキャッシュに適用済で、復帰時に
 * 自動同期される)。真にオンラインなら ack は通常即座に返るので、この上限には達しない。
 */
const ONLINE_ACK_TIMEOUT_MS = 4000;

/**
 * `promise` を上限時間と race する。`promise` が先に settle したらタイマーを片付けてその結果を
 * 伝播 (reject も伝播)。上限が先に来たら `undefined` で解決する (promise はバックグラウンド継続)。
 */
function raceWithTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), ms);
  });
  return Promise.race([
    promise.finally(() => {
      if (timer !== undefined) clearTimeout(timer);
    }),
    timeout,
  ]);
}

/**
 * createPost / sendOutboxItem 共通の Firestore + Storage 送信 helper (Blob-based)。
 *
 * 入力は **preprocess 済 Blob** を期待 (createPost 側で preprocess を済ませる、
 * outbox 経由は IndexedDB に保存済 Blob を渡す)。これにより:
 *   - online 送信と offline outbox flush で同じ Storage upload logic 共有
 *   - preprocess の二重実行を回避 (outbox 保留時に既に処理済)
 *
 * Storage upload + Firestore setDoc は atomic 風 (upload 失敗時に setDoc しない)。
 */
interface SendPostBlobs {
  districtId: string;
  authorUid: string;
  authorNickname: string;
  title: string;
  comment?: string | null;
  location: GeoPoint;
  accuracy?: number | null;
  source?: LocationSource;
  visibility?: PostVisibility;
  /** 紐づくセッション ID (user 第 2 弾、docs/04 §4-2、未紐付けは null)。 */
  sessionId?: string | null;
  /** preprocess 済 (1600px / quality 0.85) original image Blob。 */
  photoBlob?: Blob | null;
  /** preprocess 済 (400px / quality 0.8) thumbnail Blob。 */
  photoThumbBlob?: Blob | null;
  /** Blob の MIME type (通常 'image/jpeg')。 */
  photoMimeType?: string | null;
  /** 写真の幾何メタ (docs/27)。写真がある場合のみ `photo` として書込む。 */
  photoMeta?: PostPhotoMeta | null;
  /** デバイス方位サンプル (Sprint BF Part 1、GAP-U1、null/undefined なら書込まない)。 */
  orientation?: DeviceOrientationSample | null;
  /** 付帯 field 値 (Sprint BG-DataSettings 5、空オブジェクト / null なら書込まない)。 */
  extraFields?: Record<string, unknown> | null;
  /**
   * 送信先 doc ID を明示指定する (outbox の冪等送信用)。
   * 指定時はその ID の doc に setDoc する → 同一 outbox item を並行/再送しても同じ doc に上書きされ、
   * Firestore に重複 doc が作られない。online 直接投稿 (createPost) は未指定 → auto-ID を生成。
   */
  postId?: string;
  /**
   * 再送 (outbox flush / retry) フラグ。true のとき sendPostToFirestore は transaction で既存 doc の
   * createdAt と trigger 派生 field (agreeCount / aiAnalysis 等) を温存する (R4、docs/111 Wave 0)。
   * online 初回送信 (createPost) は未指定 (fresh create)。
   */
  resend?: boolean;
}

async function sendPostToFirestore(input: SendPostBlobs): Promise<string> {
  // pre-allocate post ID で Storage path 確定 → upload → setDoc の順 (atomic 感)。
  // outbox 冪等送信: postId 指定時はその ID を採用 (再送は同一 doc 上書き → 重複防止)。
  const postRef = input.postId
    ? doc(postsCollection(input.districtId), input.postId)
    : doc(postsCollection(input.districtId));
  const postId = postRef.id;

  // 再送 (outbox flush / retry) で doc が既にあれば、写真は前回の送信でサーバーへ渡っている
  // (写真は doc より先に上げる)。写真を上げ直さず、写真の項目も書かない (サーバーが公開した URL や
  // ぼかし処理の状態を上書きしない)。
  //
  // 存在判定は getDoc。送信先は本人の clientId doc なので「存在すれば本人が read 可」であり、getDoc が
  // throw する (現行 read rule は未存在 doc の get を null 参照で deny する、firestore.rules:139) のは
  // 「まだ作られていない」を意味するため catch して create 側へ倒す。transaction を使わないのは、
  // transaction の get も同 read rule で評価され未存在 doc で deny → 初回 flush が全滅するため
  // (rules を変えずに済ませる)。
  let exists = false;
  if (input.resend) {
    try {
      exists = (await getDoc(postRef)).exists();
    } catch {
      exists = false;
    }
  }

  // docs/28 §4-3: 写真は本人専用の一時置き場に上げる。サーバーが顔・ナンバーをぼかしてから公開し、
  // photoPath / photoUrl / サムネを書く。それまで doc の写真の項目は空で、ほかの参加者には写真が
  // 出ない。本人の画面は一時置き場の写真を出す (mapPostDoc)。
  let incomingPath: string | null = null;
  if (input.photoBlob && !exists) {
    incomingPath = await uploadIncomingPhoto(
      input.districtId,
      input.authorUid,
      postId,
      input.photoBlob,
      input.photoMimeType ?? 'image/jpeg',
      input.photoThumbBlob,
    );
  }

  // Sprint BF Part 1 (GAP-U1): orientation はある場合のみ書込 (undefined は Firestore で
  // 'invalid-argument' になるため、null/undefined は完全に key を出さない)。
  // createdAt は再送時の温存判定 (R4) のため別管理し、baseData には含めない。
  const baseData: Record<string, unknown> = {
    authorUid: input.authorUid,
    authorNickname: input.authorNickname,
    title: input.title,
    comment: input.comment ?? null,
    location: { latitude: input.location.latitude, longitude: input.location.longitude },
    accuracy: input.accuracy ?? null,
    source: input.source ?? 'gps',
    visibility: input.visibility ?? 'public',
    sessionId: input.sessionId ?? null,
  };
  if (!exists) {
    // 写真の URL はサーバーがぼかして公開したときに書く (docs/106 のオフライン再表示用 URL も同じ)。
    baseData.photoPath = null;
    baseData.photoThumbPath = null;
    baseData.photoUrl = null;
    baseData.photoThumbUrl = null;
    if (incomingPath) {
      // firestore.rules: 作成時に書けるのはこの形 (処理待ち + 本人の一時置き場) だけ。
      baseData.photoPrivacy = { status: 'pending', incomingPath };
    }
  }
  // docs/27: 360度写真の判定結果は写真がある場合のみ書込む (undefined は Firestore が拒否するため
  // null を埋める)。平面写真でも projection='flat' を書いて「判定済み」を表す。
  if (incomingPath && input.photoMeta) {
    baseData.photo = {
      projection: input.photoMeta.projection,
      width: input.photoMeta.width ?? null,
      height: input.photoMeta.height ?? null,
      poseHeadingDeg: input.photoMeta.poseHeadingDeg ?? null,
      detectedBy: input.photoMeta.detectedBy ?? null,
    };
  }
  if (input.orientation) {
    baseData.orientation = {
      alpha: input.orientation.alpha,
      beta: input.orientation.beta,
      gamma: input.orientation.gamma,
      capturedAt: input.orientation.capturedAt,
    };
  }
  // Sprint BG-DataSettings 5: extraFields は値があるときのみ書込 (Firestore は undefined を許容しない、
  // null は許容するが「未設定」扱いとして key を省略する方が usePosts 側の `extraFields ?? null` 読出と整合)。
  if (input.extraFields && Object.keys(input.extraFields).length > 0) {
    baseData.extraFields = input.extraFields;
  }

  if (input.resend) {
    // 再送 (outbox flush / retry)。同一 doc が既に存在しうるため、createdAt と trigger 派生 field
    // (agreeCount / aiAnalysis / updatedAt 等) を温存する (R4、docs/111 Wave 0)。
    //   - 既存: createdAt を含めず merge → createdAt も trigger 派生 field も保持
    //   - 未存在: 初回 create として createdAt=serverTimestamp を付与 (create rule を満たす)
    // 存在判定 (exists) は関数の冒頭 (写真を上げる前) で済ませている。merge:true なので TOCTOU 競合
    // (並行 flush が get→set 間に作成) でも trigger 派生 field は消えず、createdAt の巻き戻しのみ稀に
    // 残る (従来の毎回巻き戻しより大幅改善)。
    // getDoc / setDoc 失敗時は呼出元 useOutbox が attempts++ で次回 flush に再送を委ねる (消失/重複なし)。
    if (exists) {
      await setDoc(postRef, baseData, { merge: true });
    } else {
      await setDoc(postRef, { ...baseData, createdAt: serverTimestamp() }, { merge: true });
    }
  } else {
    // online 初回送信: fresh postId で doc は未存在。merge:true で書込み、万一タイムアウト降格の背景
    // send が同 doc を後追い同期しても trigger 派生 field を消さない (R4 緩和)。create rule は満たす。
    await setDoc(postRef, { ...baseData, createdAt: serverTimestamp() }, { merge: true });
  }
  return postId;
}

/**
 * 投稿作成 (REQ-4.1.10、REQ-4.1.15 offline 対応 — 完全実装)。
 *
 * 動作:
 *   - online: 直接 Firestore + Storage 送信、新規 postId を返す
 *   - offline (photo 有無問わず): outbox (IndexedDB) にキュー、`outbox:<id>` 形式の仮 ID を返す
 *     - photo 添付時は preprocess + thumbnail を生成して Blob として IndexedDB に保存
 *     - online 復帰時に sendOutboxItem が Blob を Storage upload + Firestore setDoc
 *
 * outbox は `useOutbox` hook で監視 + flush 可能。online 復帰時は自動 flush。
 */
export async function createPost(input: CreatePostInput): Promise<string> {
  const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
  // 地区フォールバック根絶: districtId は必須。未解決地区への誤投稿 (旧 phase2-default) を防ぐ。
  const districtId = input.districtId;
  if (!districtId) {
    throw new Error('createPost: districtId が未指定です (地区解決前の投稿は不可)');
  }

  // photo があれば preprocess + thumbnail を生成 (online/offline 共通、二重実行回避)
  // Task C: 地区別 photoConfig で圧縮/無圧縮/受付上限を制御。
  let photoBlob: Blob | null = null;
  let photoThumbBlob: Blob | null = null;
  let photoMimeType: string | null = null;
  if (input.photo) {
    [photoBlob, photoThumbBlob] = await Promise.all([
      preprocessPhoto(input.photo, input.photoConfig, {
        // docs/27: 360度写真は 1600px では粗すぎるため 4096px まで保持する。
        equirectangular: input.photoMeta?.projection === 'equirectangular',
      }),
      generateThumbnail(input.photo),
    ]);
    // 受付上限超過は投稿前に throw (offline outbox にも積まない)。storage.rules の全体上限が最終防衛線。
    assertPhotoSize(photoBlob, input.photoConfig);
    // uploadOriginal のときは元ファイルの MIME を維持 (PNG/HEIC 等)、それ以外は JPEG。
    // HEIC は前処理で JPEG に変換されるので、前処理後の型を使う。
    photoMimeType = input.photoConfig?.uploadOriginal
      ? photoBlob.type || input.photo.type || 'image/jpeg'
      : 'image/jpeg';
  }

  // outbox エントリ生成 (offline 経路 + online タイムアウト降格 で共有)。clientId のみ可変:
  //   - offline: newClientId() (送信時の冪等 doc ID)
  //   - online タイムアウト: pre-allocate した postId を流用し、背景 send と outbox 再送を同一 doc へ収束
  const buildOutboxEntry = (clientId: string): Omit<OutboxPost, 'id'> => ({
    clientId,
    districtId,
    authorUid: input.authorUid,
    authorNickname: input.authorNickname,
    title: input.title,
    comment: input.comment ?? null,
    location: { latitude: input.location.latitude, longitude: input.location.longitude },
    accuracy: input.accuracy ?? null,
    source: input.source ?? 'gps',
    visibility: input.visibility ?? 'public',
    sessionId: input.sessionId ?? null,
    createdLocalAt: Date.now(),
    photoBlob,
    photoThumbBlob,
    photoMimeType,
    photo: input.photoMeta ?? null,
    orientation: input.orientation ?? null,
    // 付帯 field 値 (category/タイプ 等) をオフラインでも保持し、送信時に復元する。
    extraFields: input.extraFields ?? null,
    lastError: null,
    attempts: 0,
  });

  if (!isOnline) {
    // 冪等送信用の安定 ID (送信時に Firestore doc ID として使う)。
    const id = await addToOutbox(buildOutboxEntry(newClientId()));
    return `outbox:${id}`;
  }

  // online 経路。NOTE: navigator.onLine は Android 機内モードで true のまま残るため、ここに来ても
  // 実際にはオフラインのことがある (docs/handoff_2026-06-03)。その場合 setDoc の promise はサーバ
  // ack まで解決せず、(a) フォームが固まり (b) 地図リスナーがキャッシュの pending doc を配信しない。
  // 対処:
  //   1) 送信前に doc ID を pre-allocate し「楽観 pending ピン」を登録 → ネットワーク状態に依存せず
  //      地図へ即時 pending 表示 (path C、optimisticPosts.ts 参照)。
  //   2) setDoc を上限時間と race し、ack を待たずに先へ進む (フォーム hang 回避)。
  //   3) **ack が上限内に来なければ outbox へ降格**する (Wave 0、docs/111 R-63)。旧実装は楽観的に postId を
  //      返すだけで outbox に積まず、機内モード等で setDoc がキャッシュ止まりのままタブを閉じると投稿が
  //      サイレントに消えた (R1/R2)。降格により永続化を保証し、clientId=postId の冪等送信で背景 send と
  //      outbox 再送が同一 doc に収束する (二重 doc 防止、R3)。降格時は `outbox:<id>` を返すため、PostForm は
  //      「未送信として保存しました」を表示し (偽りの成功 toast を出さない)、ユーザーの再投稿による二重も抑止。
  const postId = doc(postsCollection(districtId)).id;
  addOptimisticPost({
    id: postId,
    districtId,
    title: input.title,
    location: { latitude: input.location.latitude, longitude: input.location.longitude },
    thumbBlob: photoThumbBlob,
  });
  const send = sendPostToFirestore({
    postId,
    districtId,
    authorUid: input.authorUid,
    authorNickname: input.authorNickname,
    title: input.title,
    comment: input.comment,
    location: input.location,
    accuracy: input.accuracy,
    source: input.source,
    visibility: input.visibility,
    sessionId: input.sessionId,
    photoBlob,
    photoThumbBlob,
    photoMimeType,
    photoMeta: input.photoMeta ?? null,
    orientation: input.orientation,
    extraFields: input.extraFields,
  });
  // hard error (rules 拒否 / validation) なら楽観ピンを除去 (ghost 防止)。タイムアウト後の遅延 rejection も
  // unhandled にしないよう race とは別に catch を張る (removeOptimisticPost は冪等)。
  send.catch(() => removeOptimisticPost(postId));

  // ack が上限内に来た上での reject (online で rules 拒否 等) は await でそのまま伝播し、PostForm が
  // エラー toast を出す (楽観ピンは上の send.catch で除去済)。タイムアウト時は undefined が返る。
  const acked = await raceWithTimeout(send, ONLINE_ACK_TIMEOUT_MS);
  if (acked !== undefined) {
    // ack 受信 = サーバ確定。Firestore リスナーが同 doc を配信し MapPage が楽観ピンを dedup/除去する。
    return postId;
  }

  // 上限超過 = 実質オフライン (機内モード) or 極端な低速。outbox へ降格して投稿を失わない (R1/R2)。
  // outbox の pending マーカーが楽観ピンを置換するため、ここでピンを除去する。
  removeOptimisticPost(postId);
  const obId = await addToOutbox(buildOutboxEntry(postId));
  // 背景 send がタイムアウト後に成功したら outbox の保険を掃除する (未送信表示の残留防止)。
  // send 失敗時は outbox の自動 flush に再送を委ねる (clientId=postId で同一 doc に上書き = 冪等、想定内)。
  void send
    .then(() =>
      deleteFromOutbox(obId).catch((e) =>
        console.warn('createPost: 背景 send 成功後の outbox 掃除に失敗 (best-effort)', e),
      ),
    )
    .catch(() => {
      /* 背景 send 失敗 = outbox 再送に委ねる (想定内、ログ不要) */
    });
  return `outbox:${obId}`;
}

/**
 * outbox 1 件を Firestore に送信 (useOutbox.flush から呼出)。
 * 成功時は postId を返す (呼出側で deleteFromOutbox)。
 * 失敗時は例外を throw (呼出側で attempts++ + lastError 記録)。
 * photoBlob があれば Storage upload も同時実施 (sendPostToFirestore 内で処理)。
 */
export async function sendOutboxItem(item: OutboxPost): Promise<string> {
  // 冪等送信用 doc ID: clientId があれば採用、無い旧 entry は user 単位で衝突しない deterministic fallback。
  // 同一 item を並行/再送しても同じ doc に上書きされ、Firestore に重複 doc が作られない (1 件投稿 → 2 件 fix)。
  const postId = item.clientId ?? `ob-${item.authorUid}-${item.createdLocalAt}`;
  return await sendPostToFirestore({
    postId,
    districtId: item.districtId,
    authorUid: item.authorUid,
    authorNickname: item.authorNickname,
    title: item.title,
    comment: item.comment,
    location: item.location,
    accuracy: item.accuracy,
    source: item.source,
    visibility: item.visibility,
    sessionId: item.sessionId ?? null,
    photoBlob: item.photoBlob,
    photoThumbBlob: item.photoThumbBlob,
    photoMimeType: item.photoMimeType,
    photoMeta: item.photo ?? null,
    orientation: item.orientation ?? null,
    // 付帯 field 値 (category/タイプ 等) を復元して送信 (オフライン保存からの round-trip)。
    extraFields: item.extraFields ?? null,
    // 再送経路: 既存 doc の createdAt + trigger 派生 field を温存 (R4)。
    resend: true,
  });
}

/**
 * 投稿の編集 (REQ-4.1.17、Phase 5)。
 *
 * 編集対象 (Phase 5):
 *   - title / comment / visibility (d190a5c で実装)
 *   - photo: undefined=変更なし、null=削除、File=差し替え
 *   - location / accuracy / source (再取得)
 *   - extraFields: 投稿フォームの付帯 field 値 (投稿画面で設定できる項目を再編集可能に、本 commit で追加)
 *
 * 編集対象外:
 *   - authorUid / authorNickname / createdAt / sessionId (immutable)
 *
 * 所有者制限は firestore.rules `allow update: if isOwner(resource.data.authorUid)
 * && request.resource.data.visibility in ['public','hidden_others']` (22e616f) で server-side 強制。
 * client 側 UI で isOwner check を出した上で呼出する前提。
 *
 * Photo 差し替え動作 (atomic 観点):
 *   1. 新 File → preprocess + thumbnail 生成 → 既存 Storage path に上書き upload
 *      (path: `districts/{id}/posts/{postId}/{original,thumbnail}.jpg`)
 *   2. Storage rules `allow create, update` の create/update 両方で書込許可 (R-19、57235ae)
 *   3. Firestore photoPath/photoThumbPath はパス変わらず (overwrite で download URL token のみ変化)
 *   4. Phase 4 onPhotoUpload trigger が thumbnail を再生成 (冪等、品質保証)
 *   5. updateDoc で updatedAt のみ更新 (createPost 同様 atomic 性は維持)
 *
 * Photo 削除動作 (null):
 *   1. 既存 Storage object を best-effort delete (createPost 失敗時の dangling と同パターン)
 *   2. updateDoc で photoPath/photoThumbPath を null に
 *
 * v2 で旧と変えた点:
 *   - **partial update**: 旧は全 fields を setDoc (merge: false) だったが、v2 は updateDoc で必要 field のみ
 *   - **updatedAt 自動付与**: docs/04 §2-3 DocCommon (Phase 3 backfill 予定、R-12 完了)
 *   - **hidden_all 昇格は client UI で出さない** (firestore.rules で server-side ガード済)
 */
export interface UpdatePostInput {
  title?: string;
  comment?: string | null;
  visibility?: PostVisibility;
  /**
   * 写真の差し替え / 削除。
   * - `undefined`: 写真フィールド変更なし (既存 photoPath 維持)
   * - `null`: 写真削除 (Storage best-effort + photoPath/photoThumbPath を null に)
   * - `File`: 新画像で差し替え (preprocess + Storage upload、既存パスに上書き)
   */
  photo?: File | null;
  /** photo=File / null 時に既存 path を delete / 上書き対象として参照する。 */
  existingPhotoPath?: string | null;
  existingPhotoThumbPath?: string | null;
  /** 位置情報の再取得 (Phase 5、PostDetailModal 編集 mode の最後のピース)。 */
  location?: GeoPoint;
  accuracy?: number | null;
  source?: LocationSource;
  /**
   * 地区別 投稿写真設定 (Task C、`useDistrict().photoConfig` 素通し)。
   * photo=File 差し替え時の圧縮/無圧縮/受付上限を制御。未指定は既定で従来動作。
   */
  photoConfig?: DistrictPhotoConfig;
  /**
   * 投稿フォームの付帯 field 値 (postFields 駆動、PostExtraFieldsForm で編集した renderable field のみ)。
   *
   * **dot-path 個別更新**: ここに含まれる key だけを `extraFields.{key}` の field path で更新する
   * (`updateDoc` の dot 記法 = nested map の部分更新)。これにより AI 解析 trigger が extraFields に
   * 書き込む派生キー (aiGeneratedTitle / 各種スコア 等) を **温存** したまま、ユーザーが投稿画面で
   * 設定できる項目だけを安全に上書きできる (map 全体を setDoc/置換しない)。
   *
   * 呼出側 (PostDetailModal) は renderable な postField の key→値 のみを渡す責務を負う。
   * 値は string / number / boolean / string[] / null を想定 (undefined は書込まない)。
   */
  extraFields?: Record<string, unknown>;
}

export async function updatePost(
  districtId: string,
  postId: string,
  input: UpdatePostInput,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'posts', postId);
  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };
  if (input.title !== undefined) payload.title = input.title;
  if (input.comment !== undefined) payload.comment = input.comment;
  if (input.visibility !== undefined) payload.visibility = input.visibility;

  // 位置情報の再取得 (Phase 5、編集 mode)
  if (input.location !== undefined) {
    payload.location = {
      latitude: input.location.latitude,
      longitude: input.location.longitude,
    };
  }
  if (input.accuracy !== undefined) payload.accuracy = input.accuracy;
  if (input.source !== undefined) payload.source = input.source;

  // 付帯 field 値 (extraFields) の部分更新。
  // dot-path (`extraFields.{key}`) で渡された key のみを更新し、map 内の他 key (AI 派生キー等) を温存する。
  // undefined は Firestore が拒否するため書込まない。空文字 / null / 空配列 は「クリア」として書込む。
  if (input.extraFields) {
    for (const [key, value] of Object.entries(input.extraFields)) {
      if (value === undefined) continue;
      payload[`extraFields.${key}`] = value;
    }
  }

  // Photo 差し替え / 削除 (input.photo が指定されたときのみ)
  // R-27 反映: uploadPhoto helper で createPost / deletePost と logic 共有
  if (input.photo === null) {
    await deletePhotoFromStorage(input.existingPhotoPath, input.existingPhotoThumbPath);
    // 編集で上げたばかりの写真が一時置き場に残っていると、サーバーがあとから公開してしまうので消す。
    const selfUid = auth.currentUser?.uid;
    if (selfUid) {
      const [incoming, incomingThumb] = ownIncomingPaths(districtId, selfUid, postId);
      await deletePhotoFromStorage(incoming, incomingThumb);
    }
    payload.photoPath = null;
    payload.photoThumbPath = null;
    // docs/106: 写真削除時は URL も null に。
    payload.photoUrl = null;
    payload.photoThumbUrl = null;
    // docs/28: ぼかし処理の状態も消す (処理待ちの写真を外したときに「処理待ち」が残らないように)。
    // firestore.rules は写真を外すのと同時に限って photoPrivacy を消すのを許す。公開画像・ぼかし前の
    // 写真はサーバー (onPostWrite) も消す。
    payload.photoPrivacy = null;
  } else if (input.photo) {
    // docs/28 §4-4: 差し替えは本人専用の一時置き場に上げるだけ。サーバー (onPhotoUpload) が顔・
    // ナンバーをぼかし、公開中の写真と URL を置き換える。doc の写真の項目はここでは書かない。
    // Task C: 地区別 photoConfig で圧縮/無圧縮/受付上限を制御 (新規投稿と同じ挙動)。
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('ログイン状態を確認できませんでした。もう一度お試しください。');
    const [processed, thumb] = await Promise.all([
      preprocessPhoto(input.photo, input.photoConfig),
      generateThumbnail(input.photo),
    ]);
    assertPhotoSize(processed, input.photoConfig);
    const mimeType = input.photoConfig?.uploadOriginal
      ? processed.type || input.photo.type || 'image/jpeg'
      : 'image/jpeg';
    await uploadIncomingPhoto(districtId, uid, postId, processed, mimeType, thumb);
  }

  await updateDoc(ref, payload);
}

/**
 * 投稿削除 (REQ-4.1.17、Phase 5)。
 *
 * Firestore doc 削除 → Storage 配下 (photo / thumbnail) を best-effort 削除。
 * Storage delete 失敗時は throw せず warn (dangling reference を許容、postId 推測困難で実害低)。
 *
 * 所有者制限は firestore.rules `allow delete: if isOwner(resource.data.authorUid)` (commit 22e616f)
 * で server-side 強制される。client 側 UI で confirm を出す前提。
 */
export async function deletePost(
  districtId: string,
  postId: string,
  photoPath?: string | null,
  photoThumbPath?: string | null,
): Promise<void> {
  // Firestore 削除 (rules で本人以外は throw される)
  await deleteDoc(doc(db, 'districts', districtId, 'posts', postId));
  // Storage 削除 (R-27: deletePhotoFromStorage helper 共通化、best-effort + warn)
  await deletePhotoFromStorage(photoPath, photoThumbPath);
}

interface RawPostData {
  authorUid?: string;
  authorNickname?: string;
  title?: string;
  comment?: string | null;
  location?: GeoPoint | null;
  accuracy?: number | null;
  source?: LocationSource | null;
  visibility?: PostVisibility;
  sessionId?: string | null;
  createdAt?: Timestamp | null;
  photoPath?: string | null;
  photoThumbPath?: string | null;
  /** docs/106: 写真 download URL (オフライン再表示)。旧投稿には存在しない。 */
  photoUrl?: string | null;
  photoThumbUrl?: string | null;
  /** docs/27: 写真の幾何メタ (360度判定)。旧投稿には存在しない。 */
  photo?: PostPhotoMeta | null;
  /** docs/28: 写真のぼかし処理の状態 (サーバーが書く)。 */
  photoPrivacy?: PostPhotoPrivacy | null;
  agreeCount?: number;
  /** Phase 5.5 functions/src/ai/analyzePost.ts が書込む。shared-types で型化 (R-32 step 1)。 */
  aiAnalysis?: PostAiAnalysis | null;
  /** docs/29: 追加の観点の結果 (Functions が書く)。 */
  analysisResults?: Record<string, unknown> | null;
  /** 投稿者から管理者への再解析の依頼 (Functions の requestPostReanalysis が書く)。 */
  reanalysisRequest?: PostReanalysisRequest | null;
  /** PostDoc.extraFields (docs/04 §1-1)。Sprint X5b で score 系を expose。 */
  extraFields?: Record<string, unknown> | null;
}

function mapPostDoc(id: string, data: RawPostData, pendingWrite = false): PostMinimal {
  return {
    id,
    pendingWrite,
    authorUid: data.authorUid ?? '',
    authorNickname: data.authorNickname ?? '',
    title: data.title ?? '',
    comment: data.comment ?? null,
    location: data.location ?? null,
    accuracy: data.accuracy ?? null,
    source: data.source ?? null,
    visibility: (data.visibility ?? 'public') as PostVisibility,
    sessionId: data.sessionId ?? null,
    createdAt: data.createdAt ?? null,
    photoPath: data.photoPath ?? null,
    photoThumbPath: data.photoThumbPath ?? null,
    // docs/106: download URL を doc から読出 (旧投稿は未定義 → null、表示層が photoPath fallback)。
    photoUrl: data.photoUrl ?? null,
    photoThumbUrl: data.photoThumbUrl ?? null,
    // docs/28 §4-3: ぼかし処理を待っている写真 (新規投稿・写真の編集) の一時置き場。読めるのは本人だけ。
    // 本人の画面でだけ写真として使う (applyPhotoViewerRules)。
    photoIncomingPath: data.photoPrivacy?.incomingPath ?? null,
    photoPrivacyPublishedAt: data.photoPrivacy?.publishedAt ?? null,
    photo: data.photo ?? null,
    photoPrivacyStatus: data.photoPrivacy?.status ?? null,
    photoOriginalPath: data.photoPrivacy?.originalPath ?? null,
    photoPrivacyFinalFailure: isPhotoPrivacyFinalFailure(data.photoPrivacy),
    photoWithheld: false,
    agreeCount: Math.max(0, data.agreeCount ?? 0),
    ...mapAiAnalysisFields(data.aiAnalysis),
    ...readAnalysisResults(data.analysisResults),
    reanalysisRequested: !!data.reanalysisRequest && typeof data.reanalysisRequest === 'object',
    extraFields: data.extraFields ?? null,
  };
}

/** AI 解析の結果 (投稿本体の aiAnalysis、または追加の観点の結果) を PostMinimal の aiAnalysis* に写す。 */
export function mapAiAnalysisFields(
  ai: PostAiAnalysis | null | undefined,
): Pick<
  PostMinimal,
  | 'aiAnalysisStatus'
  | 'aiAnalysisError'
  | 'aiAnalysisEcw'
  | 'aiAnalysisBousai'
  | 'aiAnalysisWalker'
  | 'aiAnalysisRaw'
  | 'aiAnalysisAnalyzedAt'
  | 'aiAnalysisSettingsId'
  | 'aiAnalysisGeneric'
  | 'aiAnalysisGeneratedTitle'
> {
  return {
    aiAnalysisStatus: ai?.status ?? null,
    aiAnalysisError: ai?.error?.message ?? null,
    aiAnalysisEcw: ai?.ecw ?? null,
    aiAnalysisBousai: ai?.bousai ?? null,
    aiAnalysisWalker: ai?.walker ?? null,
    aiAnalysisRaw: ai?.rawOutput ?? null,
    aiAnalysisAnalyzedAt: ai?.analyzedAt ?? null,
    aiAnalysisSettingsId: ai?.settingsId ?? null,
    // 列名動的化レベル 3 (docs/105): generic は非空オブジェクトのときのみ保持 (空 {} は null 扱い)。
    aiAnalysisGeneric: ai?.generic && Object.keys(ai.generic).length > 0 ? ai.generic : null,
    aiAnalysisGeneratedTitle:
      typeof ai?.generatedTitle === 'string' && ai.generatedTitle.trim() ? ai.generatedTitle : null,
  };
}

/** docs/29: `analysisResults.{key}.aiAnalysis` を key → 結果の形で読む (壊れた要素は除く)。 */
function readAnalysisResults(
  raw: Record<string, unknown> | null | undefined,
): Pick<PostMinimal, 'analysisResults'> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, PostAiAnalysis> = {};
  const holder = { [POST_ANALYSIS_RESULTS_FIELD]: raw };
  for (const key of Object.keys(raw)) {
    const r = readPostAnalysisResult(holder, key);
    if (r) out[key] = r.aiAnalysis as PostAiAnalysis;
  }
  return Object.keys(out).length > 0 ? { analysisResults: out } : {};
}

/**
 * docs/29: 投稿を、指定した観点の結果を aiAnalysis* に持つ形に写す (投稿詳細の観点の切り替え用)。
 * 既定の観点はそのまま、結果の無い追加の観点は「未解析」の形 (status null) になる。
 */
export function projectPostAnalysis(post: PostMinimal, key: string | null): PostMinimal {
  if (isDefaultPostAnalysis(key)) return post;
  return { ...post, ...mapAiAnalysisFields(post.analysisResults?.[key as string] ?? null) };
}

/**
 * 投稿一覧を sessionId で client-side filter (user 第 3 弾、REQ-4.1.20)。
 *
 * - `currentSessionId === null`: filter なし (全件返却)
 * - `currentSessionId !== null`: `post.sessionId === currentSessionId` のもののみ返却
 *
 * server-side filter を避ける理由: 既存 usePosts は visibility 2 query merge で構成済み、
 * sessionId を追加 where 句にすると composite index 3 種が必要になる。Phase 5 規模 (1 地区 数百件)
 * では client-side filter で十分。session 数が増えて post 数が万件規模になったら index 化検討。
 */
export function filterPostsBySession(
  posts: PostMinimal[],
  currentSessionId: string | null,
): PostMinimal[] {
  if (currentSessionId === null) return posts;
  return posts.filter((p) => p.sessionId === currentSessionId);
}

/**
 * 投稿一覧を期間 (createdAt) で client-side filter (user 第 4 弾、REQ-4.1.20「期間」key)。
 *
 * - from / to が両方 null: filter なし (全件返却)
 * - from のみ: `createdAt >= from` 通過 (createdAt null は除外、旧 `?? 0` で常に範囲外と同等)
 * - to のみ: `createdAt <= to + 1 day` 通過 (旧 ListTab の「終了日 inclusive」を踏襲、end of day セマンティクス)
 * - 両方: AND
 *
 * 旧 FilterContext.tsx の dateFrom/dateTo 実装を踏襲。to は end-of-day extension で
 * 「7/15 を to に指定すると 7/15 23:59:59 まで含む」UX を維持。
 */
export function filterPostsByDateRange(
  posts: PostMinimal[],
  from: Date | null,
  to: Date | null,
): PostMinimal[] {
  if (from === null && to === null) return posts;
  const fromMs = from?.getTime() ?? null;
  const toMs = to !== null ? to.getTime() + 86400000 : null;
  return posts.filter((p) => {
    const t = p.createdAt?.toMillis() ?? 0;
    if (fromMs !== null && t < fromMs) return false;
    if (toMs !== null && t > toMs) return false;
    return true;
  });
}

/**
 * 投稿の AI 解析結果から category code 配列を mode 非依存に取得する (user 第 10 弾)。
 *
 * - ECW: aiAnalysisEcw.categoryCodes (camelCase 配列)
 * - Bousai: aiAnalysisBousai.classifications[].category_code (snake_case 各要素)
 * - Walker: aiAnalysisWalker.classifications[].category_code (同上)
 * - 未解析 / 失敗 / mode 不一致: 空配列
 *
 * mode が 3 種共に値ありの状況は通常起きない (analyzePost が 1 mode の result のみ書込) が、
 * 安全側で全 3 種を確認して flatten する。重複は呼出側で Set 化される (filterPostsByCategory 等)。
 */
export function getPostCategoryCodes(post: PostMinimal): string[] {
  const out: string[] = [];
  const ecw = post.aiAnalysisEcw?.categoryCodes;
  if (Array.isArray(ecw)) {
    for (const c of ecw) {
      if (typeof c === 'string' && c.length > 0) out.push(c);
    }
  }
  const bousai = post.aiAnalysisBousai?.classifications;
  if (Array.isArray(bousai)) {
    for (const c of bousai) {
      if (typeof c.category_code === 'string' && c.category_code.length > 0) {
        out.push(c.category_code);
      }
    }
  }
  const walker = post.aiAnalysisWalker?.classifications;
  if (Array.isArray(walker)) {
    for (const c of walker) {
      if (typeof c.category_code === 'string' && c.category_code.length > 0) {
        out.push(c.category_code);
      }
    }
  }
  // 列名動的化レベル3 / custom mode (keikan 等): generic の classifications[] から
  // 任意の `*_code` (aspect_code / object_code / category_code / gql_code 等) を集約する。
  // mode 別 result が無い custom mode でもカテゴリフィルタが効くようにする。
  const generic = post.aiAnalysisGeneric;
  if (generic && typeof generic === 'object' && !Array.isArray(generic)) {
    const cls = (generic as Record<string, unknown>).classifications;
    if (Array.isArray(cls)) {
      for (const c of cls) {
        if (c && typeof c === 'object' && !Array.isArray(c)) {
          for (const [k, v] of Object.entries(c as Record<string, unknown>)) {
            if (k.endsWith('_code') && typeof v === 'string' && v.length > 0) out.push(v);
          }
        }
      }
    }
  }
  return out;
}

/**
 * 投稿一覧を AI カテゴリで client-side filter (user 第 5 弾、REQ-4.1.20「カテゴリ」key、L3)。
 *
 * - `selectedCategoryCodes` 空配列: filter なし (全件返却、空 = 全件 の client-side 規約踏襲)
 * - 値あり: 全 mode の category code (`getPostCategoryCodes`) と OR 一致 (少なくとも 1 つ合致で通過)
 *
 * user 第 10 弾 (旧 user 第 5 弾 の bousai/walker 後追い):
 *   category source を mode 非依存 (`getPostCategoryCodes`) に切替。ECW 限定 from user 第 5 弾 →
 *   全 3 mode の AI 解析 result から category_code を取り出して OR 集計。未解析 post は対象外 (空配列)。
 *
 * 旧 (v1) 機能対応表 上は category filter 該当 UI 無し → v2 新規実装 (REQ-4.1.20 v0.2 で明文化)。
 */
export function filterPostsByCategory(
  posts: PostMinimal[],
  selectedCategoryCodes: string[],
): PostMinimal[] {
  if (selectedCategoryCodes.length === 0) return posts;
  const set = new Set(selectedCategoryCodes);
  return posts.filter((p) => {
    const codes = getPostCategoryCodes(p);
    if (codes.length === 0) return false;
    return codes.some((c) => set.has(c));
  });
}

/**
 * 写真の出し分け (docs/28 §4-3・§4-9)。地図のピン・一覧・詳細・分析ツールはすべて `usePosts` を
 * 通るので、ここ 1 か所で効く。
 *
 * - **投稿者本人**: ぼかし処理を待っている写真 (一時置き場) があればそれを出す (処理中・失敗でも本人は
 *   自分の写真を見られる、D15)。360度写真は一覧のサムネに使わない (原寸と同じ URL を no-cors の
 *   `<img>` と見回し表示の CORS 読込で共用すると端末のキャッシュで読めなくなるため)。
 * - **ほかの参加者**: ぼかしが終わっていない・失敗・公開しない写真は出さない (本文は出す、D16)。
 *   ただし一度公開した写真は、編集・作り直しの処理中 (・失敗) も前のぼかし済みの写真を出し、
 *   処理の状態 (本人向けの注意書き) は渡さない。
 *
 * @param selfUids 「自分」の uid 群 (R-70 端末引き継ぎ元を含む、`useMyIdentity().allUids`)。
 */
export function applyPhotoViewerRules(
  posts: PostMinimal[],
  selfUids: readonly string[],
): PostMinimal[] {
  return posts.map((p) => {
    if (selfUids.includes(p.authorUid)) {
      if (!p.photoIncomingPath) return p;
      return {
        ...p,
        photoPath: p.photoIncomingPath,
        // 一覧・地図には端末が一緒に上げた小さいサムネを使う (原寸を使わない、レビュー A4。
        // 360度写真で原寸の URL を no-cors の <img> と見回し表示で共用する問題も避けられる)。
        photoThumbPath: incomingThumbPathOf(p.photoIncomingPath),
        photoUrl: null,
        photoThumbUrl: null,
      };
    }
    if (isPhotoStatusPublished(p.photoPrivacyStatus)) return p;
    if (isPhotoStatusPublished(p.photoPrivacyStatus, p.photoPrivacyPublishedAt) && p.photoPath) {
      // 前のぼかし済みの写真を出す。処理中の注意書きは本人向けなので渡さない。
      return { ...p, photoPrivacyStatus: null, photoPrivacyFinalFailure: false };
    }
    return {
      ...p,
      photoPath: null,
      photoThumbPath: null,
      photoUrl: null,
      photoThumbUrl: null,
      photoWithheld: true,
    };
  });
}

/**
 * PDF 帳票など外に出す物に使う投稿から、本人だけが見られるぼかす前の写真 (一時置き場) を外す
 * (docs/28 レビュー A5)。本人の画面では一時置き場の写真を出しているため、そのまま帳票に載せると
 * ぼかす前の写真が配布されてしまう。
 */
export function withoutUnpublishedOwnPhotos(posts: readonly PostMinimal[]): PostMinimal[] {
  return posts.map((p) =>
    p.photoIncomingPath
      ? { ...p, photoPath: null, photoThumbPath: null, photoUrl: null, photoThumbUrl: null }
      : p,
  );
}

/**
 * 投稿一覧 subscribe (旧 lib/posts.ts:192-198 「2 query merge」パターン踏襲)。
 *
 * - public な全投稿
 * - 自分の hidden_others 投稿
 *
 * の和集合を createdAt desc で返す。hidden_all は管理画面側のみ対象 (REQ-4.1.18)。
 *
 * @param currentUid 現在の認証ユーザー uid (anonymous も含む)。null/undefined 時は public のみ。
 */
export function usePosts(
  currentUid: string | null | undefined,
  districtId: string | null,
  /**
   * 再試行用 key (Sprint AM、UI2 無限スピナー解消)。
   * 呼出側で `setRetryKey(k => k+1)` するたびに useEffect が re-run され、
   * subscribe を貼り直す。値は依存配列に含まれるのみで内部で使われない。
   */
  retryKey: number = 0,
  /**
   * 「自分」の uid 群 (R-70 端末引き継ぎ元を含む、`useMyIdentity().allUids`)。docs/28: ぼかしが
   * 終わっていない写真を本人以外に出さない判定に使う。省略時は `currentUid` だけ。
   */
  selfUids?: readonly string[],
): PostsState {
  const [state, setState] = useState<PostsState>({
    status: 'loading',
    posts: [],
    error: null,
  });
  // 配列の参照が毎回変わっても購読を張り直さないよう、中身で比較できる文字列にする。
  const selfKey = (selfUids && selfUids.length > 0 ? [...selfUids] : currentUid ? [currentUid] : [])
    .sort()
    .join(',');

  useEffect(() => {
    // retry 時に必ず loading に戻す (前回の error / ready を表示し続けないため)。
    setState({ status: 'loading', posts: [], error: null });
    // 地区フォールバック根絶: districtId 未解決 (null/空) のときは phase2-default の
    // posts を購読しない (迷子投稿 = 誤地区への読み書きを防ぐ)。呼出側ページが readiness で gate。
    if (!districtId) return;
    const col = postsCollection(districtId);
    const subscriptions: Unsubscribe[] = [];
    let publicPosts: PostMinimal[] = [];
    let myHiddenPosts: PostMinimal[] = [];
    let publicReady = false;
    let mineReady = !currentUid; // currentUid 無ければ mine 不要、即 ready 扱い

    function pushState(error?: Error) {
      if (error) {
        setState({ status: 'error', posts: [], error });
        return;
      }
      if (!(publicReady && mineReady)) return;
      const merged = [...publicPosts, ...myHiddenPosts].sort((a, b) => {
        const ta = a.createdAt?.toMillis() ?? 0;
        const tb = b.createdAt?.toMillis() ?? 0;
        return tb - ta;
      });
      setState({
        status: 'ready',
        posts: applyPhotoViewerRules(merged, selfKey ? selfKey.split(',') : []),
        error: null,
      });
    }

    // public 全件。includeMetadataChanges: true で hasPendingWrites の変化 (ローカル書込→サーバ ack)
    // も snapshot に流す → 未送信 (オフラインキャッシュ済) 投稿を pendingWrite=true で識別できる。
    subscriptions.push(
      onSnapshot(
        query(col, where('visibility', '==', 'public'), orderBy('createdAt', 'desc')),
        { includeMetadataChanges: true },
        (snap) => {
          publicPosts = snap.docs.map((d) =>
            mapPostDoc(d.id, d.data() as RawPostData, d.metadata.hasPendingWrites),
          );
          publicReady = true;
          pushState();
        },
        (err) => pushState(err),
      ),
    );

    // 自分の hidden_others
    if (currentUid) {
      subscriptions.push(
        onSnapshot(
          query(
            col,
            where('visibility', '==', 'hidden_others'),
            where('authorUid', '==', currentUid),
            orderBy('createdAt', 'desc'),
          ),
          { includeMetadataChanges: true },
          (snap) => {
            myHiddenPosts = snap.docs.map((d) =>
              mapPostDoc(d.id, d.data() as RawPostData, d.metadata.hasPendingWrites),
            );
            mineReady = true;
            pushState();
          },
          (err) => pushState(err),
        ),
      );
    }

    return () => {
      subscriptions.forEach((u) => u());
    };
  }, [currentUid, districtId, retryKey, selfKey]);

  return state;
}
